import type { AutocompleteRenderOptionState } from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import { AutoCompleteVirtualize } from '@openx/components-core/lib/AutoComplete/AutoCompleteVirtualize.tsx';
import type { HierarchyItem, HierarchyItemWithLevels } from '@openx/types/hierarchyItem.ts';
import type { StateUtilMap } from '@openx/types/stateUtil.ts';
import { type JSX, useCallback, useMemo } from 'react';
import { OptionItem } from './components/OptionItem.tsx';
import { useHierarchyData } from './hooks/useHierarchyData.ts';
import { useHierarchyState } from './hooks/useHierarchyReducer.ts';
import { flattenWithLevels } from './utils/flattenWithLevels.ts';

type HierarchyAutoCompleteProps = {
  data: HierarchyItem[];
  dataMap?: StateUtilMap<HierarchyItem>;
  isLoading: boolean;
  inputLabel?: string;
  onOptionSelect: (item: HierarchyItemWithLevels | null) => void;
  testId?: string;
  value: string;
  placeholderText: string | null;
  noOptionsText: () => JSX.Element;
};

export const HierarchyAutoComplete = ({
  data = [],
  dataMap = {},
  isLoading = true,
  inputLabel = '',
  onOptionSelect,
  testId,
  value,
  placeholderText,
  noOptionsText,
}: HierarchyAutoCompleteProps) => {
  const initialName: string | undefined = useMemo(
    () => dataMap?.[value]?.name ?? flattenWithLevels(data).find(el => el.id === value)?.name,
    [value, data, dataMap]
  );
  const { inputPhrase, onOptionAdd, onClose, handleInputChange } = useHierarchyState({
    initialName,
    onOptionSelect,
    value,
  });
  const { inventories, filterOptions, getNoOptionsText } = useHierarchyData(
    data,
    inputPhrase,
    isLoading,
    noOptionsText
  );

  const renderOption = useCallback((props, option: HierarchyItemWithLevels, state: AutocompleteRenderOptionState) => {
    const { key, ...rest } = props;

    return (
      <Box key={key} component="li" {...rest}>
        <OptionItem option={option} searchPhrase={state.inputValue} />
      </Box>
    );
  }, []);

  const inputValue: HierarchyItemWithLevels | null = useMemo(
    () => inventories.find(el => el.id === value) ?? null,
    [inventories, value]
  );

  return (
    <AutoCompleteVirtualize
      options={inventories}
      renderOption={renderOption}
      getOptionLabel={option => option.name || ''}
      data-test={testId}
      onInputChange={handleInputChange}
      textFieldProps={{
        label: inputLabel,
        margin: 'dense',
        placeholder: placeholderText ?? '',
      }}
      onChange={onOptionAdd}
      onClose={onClose}
      filterOptions={filterOptions}
      loading={isLoading}
      noOptionsText={getNoOptionsText()}
      inputValue={inputPhrase}
      value={inputValue}
    />
  );
};
